import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X,
  Plus,
  Search,
  Radio,
  Settings,
  HelpCircle,
  Database,
  ChevronRight,
  Folder,
  KanbanSquare,
} from 'lucide-react';
import { Dataset, IdealCustomerProfile } from '../../../types';
import { getTableTypeIcon, getDatasetStatusIndicator } from '../../../utils/tableUtils';
import { PLAN_LIMITS } from '../../../constants/layout';
import { useSettingsModal } from '../../../contexts/SettingsModalContext';
import { useAuth } from '../../../contexts/AuthContext';
import { isPipelineViewActive } from '../../../utils/adminView';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  datasets: Dataset[];
  selectedDatasetId?: string;
  icpProfiles: IdealCustomerProfile[];
  expandedIcpIds: Set<string>;
  isICPSectionExpanded: boolean;
  creditData: { current_month_credits?: number } | null;
  planName: string;
  onToggleIcp: (id: string) => void;
  onToggleICPSection: () => void;
  onOpenSearch: () => void;
  onOpenWizard: () => void;
  onOpenWithIcp: (icp: IdealCustomerProfile) => void;
  onOpenBilling: () => void;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  datasets,
  selectedDatasetId,
  icpProfiles,
  expandedIcpIds,
  isICPSectionExpanded,
  creditData,
  planName,
  onToggleIcp,
  onToggleICPSection,
  onOpenSearch,
  onOpenWizard,
  onOpenWithIcp,
  onOpenBilling,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openSettings } = useSettingsModal();

  const isOnHome = location.pathname === '/' || location.pathname === '/datasets';
  // Shared with AppSidebar + AnalyticsTabs — one predicate, no drift.
  const isPipelineTabActive = isPipelineViewActive(location.pathname, location.search);

  const navItems = [
    { id: 'search', label: 'Search', icon: Search, action: onOpenSearch, isActive: false, dataTour: undefined },
    { id: 'new-task', label: 'New Task', icon: Plus, path: '/datasets', isActive: isOnHome && !selectedDatasetId, dataTour: undefined },
    // Operator-only surface — hidden for non-admins so the entry is never a dead end.
    ...(user?.isAdmin ? [{ id: 'pipeline', label: 'Pipeline', icon: KanbanSquare, path: '/analytics?view=pipeline', isActive: isPipelineTabActive, dataTour: undefined }] : []),
    { id: 'monitors', label: 'Monitors', icon: Radio, action: () => { onClose(); openSettings('monitors'); }, isActive: false, dataTour: 'mobile-nav-monitors' },
    { id: 'settings', label: 'Settings', icon: Settings, action: () => { onClose(); openSettings(); }, isActive: false, dataTour: 'mobile-nav-settings' },
  ];

  const totalCredits = PLAN_LIMITS[planName] || 100;
  const remainingCredits = creditData?.current_month_credits || 0;
  const remainingPercentage = Math.min(100, totalCredits > 0 ? (remainingCredits / totalCredits) * 100 : 0);

  return (
    <>
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-50"
          onClick={onClose}
        />
      )}

      <div
        className={`md:hidden fixed top-0 right-0 h-full w-[85vw] max-w-[360px] bg-white z-50 transform transition-transform duration-300 ease-out shadow-xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-neutral-100">
          <span className="text-[15px] font-semibold text-neutral-900">Menu</span>
          <button
            onClick={onClose}
            className="p-2.5 -mr-2 text-neutral-500 active:text-neutral-700 active:bg-neutral-100 rounded-xl transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col h-[calc(100%-56px-env(safe-area-inset-top,0px))] overflow-hidden">
          <div className="px-3 py-3 border-b border-neutral-100 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-tour={item.dataTour}
                  onClick={() => {
                    if (item.action) {
                      item.action();
                    } else if (item.path) {
                      navigate(item.path);
                      onClose();
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition ${
                    item.isActive
                      ? 'bg-neutral-100 text-neutral-900'
                      : 'text-neutral-600 active:bg-neutral-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    item.isActive ? 'bg-neutral-900' : 'bg-neutral-100'
                  }`}>
                    <Icon className={`w-4 h-4 ${item.isActive ? 'text-white' : 'text-neutral-600'}`} />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {icpProfiles.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between px-3 mb-2">
                  <button
                    onClick={onToggleICPSection}
                    className="flex items-center gap-2"
                  >
                    <ChevronRight
                      className={`w-3 h-3 text-neutral-400 transition-transform ${isICPSectionExpanded ? 'rotate-90' : ''}`}
                    />
                    <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wide">
                      ICPs
                    </span>
                    <span className="text-2xs text-neutral-400">{icpProfiles.length}</span>
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/datasets');
                      onOpenWizard();
                    }}
                    className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition"
                    title="Ask AI for ideas"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {isICPSectionExpanded && (
                  <div className="space-y-1">
                    {icpProfiles.map((icp) => {
                      const icpDatasets = datasets.filter(d => d.icp_id === icp.id);
                      const isExpanded = expandedIcpIds.has(icp.id);

                      return (
                        <div key={icp.id}>
                          <div className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-neutral-600 hover:bg-neutral-50 transition">
                            <button
                              onClick={() => onToggleIcp(icp.id)}
                              className="flex items-center gap-3 flex-1 min-w-0"
                            >
                              <Folder className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                              <span className="flex-1 text-sm truncate text-left">{icp.name}</span>
                            </button>
                            {icpDatasets.length > 0 ? (
                              <span className="text-2xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                {icpDatasets.length}
                              </span>
                            ) : (
                              <button
                                onClick={() => {
                                  onClose();
                                  navigate('/datasets');
                                  onOpenWithIcp(icp);
                                }}
                                className="p-1 text-neutral-400 hover:text-blue-500 hover:bg-blue-50 rounded transition flex-shrink-0"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => onToggleIcp(icp.id)}
                              className="flex-shrink-0"
                            >
                              <ChevronRight
                                className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              />
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="ml-8 pl-3 border-l border-neutral-200 mt-1 mb-2">
                              {icpDatasets.length === 0 ? (
                                <button
                                  onClick={() => {
                                    onClose();
                                    navigate('/datasets');
                                    onOpenWithIcp(icp);
                                  }}
                                  className="flex items-center gap-2 py-2 px-2 text-neutral-400 hover:text-blue-500 transition"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  <span className="text-xs">Generate signals</span>
                                </button>
                              ) : (
                                icpDatasets.map((dataset) => (
                                  <button
                                    key={dataset.id}
                                    onClick={() => {
                                      navigate(`/datasets/${dataset.id}`);
                                      onClose();
                                    }}
                                    className={`w-full flex items-center gap-2 py-2 px-2 rounded-md transition ${
                                      selectedDatasetId === dataset.id
                                        ? 'bg-neutral-100 text-neutral-900'
                                        : 'text-neutral-600 hover:bg-neutral-50'
                                    }`}
                                  >
                                    <Database className="w-4 h-4 text-blue-500" />
                                    <span className="flex-1 text-13 truncate text-left">{dataset.name}</span>
                                    {dataset.lead_count !== undefined && dataset.lead_count > 0 && (
                                      <span className="text-2xs text-neutral-400">{dataset.lead_count}</span>
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 px-3 mb-2" data-tour="mobile-datasets-section">
              <Database className="w-4 h-4 text-neutral-400" />
              <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wide">
                Recent Datasets
              </span>
            </div>

            {datasets.length > 0 ? (
              <div className="space-y-1">
                {datasets.slice(0, 10).map((dataset) => {
                  const TypeIcon = getTableTypeIcon(dataset.table_type);
                  const status = getDatasetStatusIndicator(dataset.status);
                  const StatusIcon = status.icon;
                  const isSelected = selectedDatasetId === dataset.id;

                  return (
                    <button
                      key={dataset.id}
                      onClick={() => {
                        navigate(`/datasets/${dataset.id}`);
                        onClose();
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition ${
                        isSelected
                          ? 'bg-neutral-100 text-neutral-900'
                          : 'text-neutral-600 hover:bg-neutral-50'
                      }`}
                    >
                      <div className="relative flex-shrink-0">
                        <TypeIcon className="w-5 h-5" />
                        {(dataset.status === 'generating' || dataset.status?.startsWith('error')) && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full flex items-center justify-center">
                            <StatusIcon
                              className={status.spinning ? 'animate-spin' : ''}
                              style={{ width: 8, height: 8, color: status.color }}
                            />
                          </div>
                        )}
                      </div>
                      <span className="flex-1 text-sm truncate text-left">{dataset.name}</span>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 px-4">
                <Database className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                <p className="text-13 text-neutral-500">No datasets yet</p>
              </div>
            )}
          </div>

          {creditData && (
            <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50">
              <button onClick={onOpenBilling} className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xs font-medium text-neutral-500 uppercase tracking-wide">
                    Credits
                  </span>
                  <span className="text-2xs text-neutral-400">{planName}</span>
                </div>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="text-lg font-medium text-neutral-900">
                    {remainingCredits.toLocaleString()}
                  </span>
                  <span className="text-13 text-neutral-500">remaining</span>
                </div>
                <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-neutral-500 rounded-full"
                    style={{ width: `${remainingPercentage}%` }}
                  />
                </div>
              </button>
            </div>
          )}

          <div className="px-3 py-3 border-t border-neutral-100" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
            <button
              onClick={() => window.open('https://calendly.com/phillipan', '_blank')}
              className="w-full flex items-center gap-3 px-3 py-3 text-neutral-600 active:bg-neutral-50 rounded-xl transition"
            >
              <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-neutral-600" />
              </div>
              <span className="text-sm font-medium">Help & Support</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
